import { Component, OnInit, Renderer2,  ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { catchError, combineLatest, of, Subscription } from 'rxjs';
import { AvailableThesaurusTerms } from '@interfaces/available-thesaurus-terms.interface';
import { Thesaurus } from '@interfaces/thesaurus.interface';
import { ThesaurusEntry } from '@interfaces/thesaurus-entry.interface';
import { ThesaurusService } from '@thesaurus-service/thesaurus.service';
import { ThesaurusTerm } from '@interfaces/thesaurus-term.interface';
import { TermsService } from '@terms-service/terms.service';
import i18next from 'i18next';
import { EventsService } from 'app/services/events/events.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AvailableThesaurusCategories } from '../../interfaces/available-thesaurus-categories.interface';

@Component({
    selector: 'app-thesaurus-index',
    templateUrl: './thesaurus-index.component.html',
    styleUrl: './thesaurus-index.component.scss',
    standalone: false
})
export class ThesaurusIndexComponent implements OnInit {
  @ViewChild('headerInfoIcon') headerInfoIcon!: ElementRef;
  @ViewChild('searchForm') searchForm!: NgForm;
  @Input() token!: string;
  @Input() term?: string;
  @Output() browseTermsClick = new EventEmitter<{ thesaurusId: string, availableTerm: string}>();
  @Output() browseCategoriesClick = new EventEmitter<{ thesaurusId: string, availableCategory: string}>();
  @Output() searchTermsClick = new EventEmitter<{ thesaurusId: string, searchTerm: string, searchOption:string, searchCategory?:string, searchType?: string}>();
  @Output() clearTermsClick = new EventEmitter<{ thesaurusId: string}>();
  thesaurus?: Thesaurus;
  availableThesaurusTerms?: AvailableThesaurusTerms;
  availableThesaurusCategories?: AvailableThesaurusCategories;
  subscription: any;
  displayBanner: boolean = true;
  selectedTerm?: string;
  thesaurusEntries: ThesaurusEntry[] = [];
  serviceErrorMsg: string = ""; //Placeholder property in case we decide to customize error messages
  componentContent: string = "terms"; //Content presented by this component
  allEntries: string = "All";
  rootEntry: string = "Root";
  alphaTermsList: string [] = ["0-9","A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z"];
  termsCollection: ThesaurusTerm[] = [];
  searchOptions: {name:string, default:boolean, value:string}[] = [
    {name: 'Contains word(s)', default:true, value: 'contains'},
    {name: 'Begins with (enter at least 2 characters)', default:false, value: 'begins'}];
  searchOption: string = "contains";
  searchTerm: string = "";
  searchCategory: string = "";
  searchType: string = ""; //For PQ-93633
  
  ariaLabelLexicon: string = "Lexicon search filtering options";
  lexiconOptions = [
    { value: 'all', label: 'All' },
    { value: 'index', label: 'Index only' },
    { value: 'dictionary', label: 'Dictionary only' }
  ];
  ariaLabelCorporate: string = "Select a company type";
  corporateSearchTypes = [
    { value: 'all', label: 'All' },
    { value: 'private', label: 'Private' },
    { value: 'public', label: 'Public' },
    { value: 'subsidiary', label: 'Subsidiary' },
    { value: 'government-owned', label: 'Government-owned' }
  ];
  businessCategories: string [] = ["ACCTAX", "BANKING", "BUSINESSLAW", "ECONOMICS", "ENTREP", "FINANCE", "HUMANRESOURCES", "INDUSTRY", "INSURANCE", "MANAGEMENT", "MARKETING", "OPERATIONS"];

  constructor(
    private thesaurusService: ThesaurusService,
    private route: ActivatedRoute,
    public termsService: TermsService,
    private renderer: Renderer2, 
    private eventsService: EventsService, 
    private spinnerService: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.subscription = combineLatest([this.route.params, this.route.queryParams])
      .subscribe(([params, queryParams]) => {
        this.setThesaurus(params['thesaurusId'], queryParams['token'], queryParams['selecteddatabases'], queryParams['thesaurus']);
      });
      this.searchOption = "contains";
      this.setSearchCategoryOrSearchType();
      this.eventsService.getShowIndexSpinner().subscribe(() => {
          this.showIndexSpinner();
      });
      this.eventsService.getHideIndexSpinner().subscribe(() => {
            this.hideIndexSpinner();
      });
  }

  ngAfterViewInit(): void {
    this.setSkipLinkTop();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  //Set a new value for the #SkipPage's "top" CSS attribute. Only applies for the Lexicon, Corporation, and Business thesauri. (A default value for "top" is defined in thesaurus-index.component.scss and is applied to all other thesauri.)
  setSkipLinkTop(): void {
    if (this.isLexiconThesaurus()) {
      document.documentElement.style.setProperty('--skip-link-top', '265px');
    } else {
      document.documentElement.style.setProperty('--skip-link-top', '150px');
    }
  }

  setThesaurus(thesaurusId: string, token: string, selecteddatabases: string, selectedthesaurus: string): void {
    this.thesaurusService.getThesauri(token, selecteddatabases, selectedthesaurus).pipe(
              catchError(error => {
                this.thesaurusService.setThesaurusServiceError(true);
                // Handle the error (e.g., return an empty array, rethrow a specific error)
                return of([]); // Or throw new Error('Custom error');
              })
    ).subscribe(
      thesauri => {
        this.displayBanner = thesauri.length > 1;
        this.thesaurus = thesauri.find(thesaurus => thesaurus.id === thesaurusId);
        if (this.thesaurus) {
          this.setAvailableTerms(this.thesaurus, token);
          this.setAvailableCategories(this.thesaurus, token);
        }
      }
    );
  }

  //Pass the clicked-on term and the thesaurusID to the parent component (thesaurus-terms). Add the 'active' class to the HTML element that was clicked on to trigger CSS changes.
  onBrowseTermsClick(event: Event, thesaurusId: string, availableTerm: string) {
    event.preventDefault();

    // Clear any remnants of a previous search
    this.searchTerm = "";
    this.searchOption = "contains";
    this.searchType = "all";
    this.searchCategory = "";

    const element = event.target as HTMLElement;
    this.removeBrowseTermsActiveClass(element);
    this.removeAllBrowseCategoriesActiveClass();

    this.browseTermsClick.emit({ thesaurusId, availableTerm });
    element.classList.add('active');
  }
  //Remove active class from element when it is not needed
  removeBrowseTermsActiveClass(element: HTMLElement): void {
    const elements = document.querySelectorAll('[id^="browseTerms-"]');

    elements.forEach((el) => {
      if (el.classList.contains('active') && el.id !== element.id) {
        this.renderer.removeClass(el, 'active');
      }
    });
  }
  
  removeAllBrowseTermsActiveClass(): void {
    const elements = document.querySelectorAll('[id^="browseTerms-"]');

    elements.forEach((el) => {
      if (el.classList.contains('active')) {
        this.renderer.removeClass(el, 'active');
      }
    });
  }

  onBrowseCategoriesClick(event: Event, thesaurusId: string, availableCategory: string) {
    event.preventDefault();

    // Clear any remnants of a previous search
    this.searchTerm = "";
    this.searchOption = "contains";
    this.searchType = "all";

    const element = event.target as HTMLElement;
    this.removeBrowseCategoriesActiveClass(element);
    this.removeAllBrowseTermsActiveClass();

    this.browseCategoriesClick.emit({ thesaurusId, availableCategory });
    document.getElementById(element.id)?.parentElement?.classList.remove('category-list-item');
    document.getElementById(element.id)?.parentElement?.classList.add('category-list-item-active');
    element.classList.add('category-link-active');
  }

  removeBrowseCategoriesActiveClass(element: HTMLElement): void {
    const elements = document.querySelectorAll('[id^="browseCategories-"]');

    elements.forEach((el) => {
      this.renderer.removeClass(document.getElementById(el.id)?.parentElement, 'category-list-item-active');
      this.renderer.addClass(document.getElementById(el.id)?.parentElement, 'category-list-item');
      this.renderer.removeClass(el, 'category-link-active');
      this.renderer.addClass(el, 'category-link');
    });
  }
  
  removeAllBrowseCategoriesActiveClass(): void {
    const elements = document.querySelectorAll('[id^="browseCategories-"]');

    elements.forEach((el) => {
      this.renderer.removeClass(document.getElementById(el.id)?.parentElement, 'category-list-item-active');
      this.renderer.addClass(document.getElementById(el.id)?.parentElement, 'category-list-item');
      this.renderer.removeClass(el, 'category-link-active');
      this.renderer.addClass(el, 'category-link');
    });
  }

  setAvailableTerms(thesaurus: Thesaurus, token: string): void {
    this.thesaurusService.getAvailableThesaurusTerms(thesaurus, token).subscribe(
      availableTerms => {
        this.availableThesaurusTerms = availableTerms;
        this.validateTermsCollection(this.availableThesaurusTerms.availableTerms, thesaurus.id);
      }
    );
  }

  setAvailableCategories(thesaurus: Thesaurus, token: string): void {
    this.thesaurusService.getAvailableThesaurusCategories(thesaurus, token).subscribe(
      availableCategories => {
        this.availableThesaurusCategories = availableCategories;
      }
    );
  }

  isLexiconThesaurus(): boolean {
    if (this.thesaurus?.id) {
      return this.thesaurusService.isLexiconThesaurus(this.thesaurus?.id);
    }
    return false;
  }

  isCorporationThesaurus(): boolean {
    if (this.thesaurus?.id) {
      return this.thesaurusService.isCorporationThesaurus(this.thesaurus?.id);
    }
    return false;
  }

  isBusinessThesaurus(): boolean {
    if (this.thesaurus?.id) {
      return this.thesaurusService.isBusinessThesaurus(this.thesaurus?.id);
    }
    return false;
  }

  getDropdownLabel(label: string): string {
    return i18next.t(label);
  }

  isServiceError(): boolean {
    return this.thesaurusService.isThesaurusServiceError();
  }

  isServiceErrorSource(): boolean {
    return this.thesaurusService.getServiceErrorSource() === this.componentContent;
  }

  //When terms are not available for a specific alpha-numeric character, that character will be ommitted from the list of terms that's returned by the gateway. This function checks for missing terms and adds them back in for UI display. These "added" terms will display as disabled links in the "Browse Terms" section of the page
  validateTermsCollection(terms: ThesaurusTerm[], thesaurusId: string): void {
    this.termsCollection = terms;
    this.alphaTermsList.forEach((index, value)=>  {
      if (this.alphaTermsList.length > this.termsCollection.length) {
        const termPresent = this.termsCollection.some((item => item.display === this.alphaTermsList[value]));

        if (!termPresent) {
          this.termsCollection.splice(value, 0, {facet: "", display: this.alphaTermsList[value], displayUrl: false});
        }
      }
    })
  }

  onOptionChange(option: string): void {
    this.searchOption = option;
  }

  onSearchCategoryChange(updatedCategory: string): void {
    this.searchCategory = updatedCategory; 
  }

  onSearchTypeChange(updatedType: string): void {
    this.searchType = updatedType; 
  }


  //Set searchCategory or searchType to default values, depending on selected thesaurus.
  setSearchCategoryOrSearchType(): void {
    if (this.isLexiconThesaurus()) this.searchCategory = "all";
    if (this.isCorporationThesaurus()) this.searchType = "all";
  }

  findSearchTerm(event: Event, thesaurusId: string, searchTerm: string, searchOption: string, searchCategory?: string, searchType?: string): void {
    event.preventDefault();

    if (!this.thesaurus?.id) {
        console.error('Thesaurus ID is undefined');
        return;
    }

    this.removeAllBrowseTermsActiveClass();
    this.removeAllBrowseCategoriesActiveClass();
    this.searchTermsClick.emit({ thesaurusId, searchTerm, searchOption, searchCategory, searchType });
  }

  onClearSearchTermsClick(event: Event): void {
    event.preventDefault();
    this.removeAllBrowseTermsActiveClass();
    this.removeAllBrowseCategoriesActiveClass();
    this.setSearchCategoryOrSearchType();
    this.searchTerm = "";
    this.searchOption = "contains";
    let thesaurusId = "";
    this.termsService.clearList();

    this.clearTermsClick.emit({thesaurusId});
  }

  //Accessibility: when a user clicks or hits enter key on the "Skip A to Z" button, set focus on the first tabbable element that follows the "Browse Terms" alpha-numeric characters.
  public onSkipAToZ(event: Event): void {
    event.preventDefault();
  
    const browseList = document.getElementById('browseTermsList');
    if (browseList) {
      this.focusNextTabbableAfter(browseList);
    }
  }

  private focusNextTabbableAfter(anchor: Element): void {
    const selector = [
      'a[href]:not([tabindex="-1"]):not([aria-disabled="true"]):not(.disabled)',
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const candidates = Array.from(document.querySelectorAll<HTMLElement>(selector))
      .filter(el => el.offsetParent !== null); // basic “visible” check

    const next = candidates.find(el =>
      !anchor.contains(el) &&
      !!(anchor.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)
    );

    next?.focus();
  }

  showIndexSpinner() {
    this.spinnerService.show("index-spinner");
    this.eventsService.emitDisableHelpButtonEvent(true);
  }

  hideIndexSpinner() {
    this.spinnerService.hide("index-spinner");
    this.eventsService.emitDisableHelpButtonEvent(false);
  }
}
